import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VerifiedIcon from '@mui/icons-material/Verified';
import { DESIGN_TOKENS } from '../theme';

const UPLOAD_STEP = {
  icon: <CloudUploadIcon sx={{ fontSize: 22, color: '#6366f1' }} />,
  iconBg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
  accent: '#6366f1',
  title: 'You upload your portfolio',
};

const CYCLE_STEPS = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />,
    iconBg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    accent: '#8b5cf6',
    title: 'Reads & organises',
    desc: 'Mill reads every PDF, extracts expiry dates and property addresses, and builds your compliance portfolio automatically.',
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 20, color: '#0ea5e9' }} />,
    iconBg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    accent: '#0ea5e9',
    title: 'Sets reminders',
    desc: 'Mill creates calendar events and internal reminders weeks before each expiry — enough runway to act before the deadline.',
  },
  {
    icon: <MailOutlineIcon sx={{ fontSize: 20, color: '#f59e0b' }} />,
    iconBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    accent: '#f59e0b',
    title: 'Contacts your provider',
    desc: 'When a renewal window opens, Mill sends a personalised email to the right contractor for each property.',
  },
  {
    icon: <EventAvailableIcon sx={{ fontSize: 20, color: DESIGN_TOKENS.success }} />,
    iconBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    accent: DESIGN_TOKENS.success,
    title: 'Books the appointment',
    desc: 'Mill manages the conversation with your contractor until an appointment is booked — no missed deadlines, no fines.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 20, color: '#6366f1' }} />,
    iconBg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    accent: '#6366f1',
    title: 'Renews your record',
    desc: "The provider's renewed certificate is picked up from your inbox, validated, and replaces the old one automatically.",
  },
];

// Circle geometry — scaled 32% from original (20% + 10%)
const CONTAINER = 818;
const CX = 409;
const CY = 409;
const RADIUS = 251;
const CIRCUMFERENCE = Math.round(2 * Math.PI * RADIUS); // 1577
const CARD_W = 189;
const CARD_H = 87;
const CARD_H_HALF = CARD_H / 2;
const CONNECTOR_H = 44;
const MT_PULL = 80; // negative margin-top applied to circle box
// Distance from circle-box top to first cycle card top edge
const INNER_BRIDGE = CY - RADIUS - CARD_H_HALF; // 108
// y1 of bridge line in circle-box coords = where upload card bottom sits inside the circle box
const BRIDGE_Y1 = MT_PULL - CONNECTOR_H; // 36

const AIFlowPage: React.FC = () => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const navigate = useNavigate();
  const { palette } = useTheme();

  return (
    <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Typography onClick={() => navigate('/ai-flow2')} sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          View variant 2 →
        </Typography>
      </Box>
      <style>{`
        @keyframes aiFlowDown { to { stroke-dashoffset: -20; } }
        @keyframes circleFlowCW { to { stroke-dashoffset: -${CIRCUMFERENCE}; } }
      `}</style>

      <Box sx={{ display: { xs: 'block', lg: 'flex' }, alignItems: 'flex-start' }}>

        {/* ── LEFT ── */}
        <Box sx={{ flex: '0 0 50%', position: { lg: 'sticky' }, top: { lg: 24 }, pt: { xs: 8, lg: 22 }, pr: { lg: 8 }, pl: { xs: 1, lg: 4 }, mb: { xs: 5, lg: 0 } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#f0f4ff', borderRadius: 10, px: 2, py: 0.6, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.tokens.primary }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: palette.tokens.primary, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Mill, your AI agent
            </Typography>
          </Box>
          <Typography variant="displayXl" sx={{ color: palette.tokens.ink, lineHeight: 1.05, mb: 2.5, letterSpacing: -2, fontSize: { xs: '2.5rem', md: '4.3rem' } }}>
            Your compliance,{' '}
            <Box component="span" sx={{ color: palette.tokens.primary }}>fully automated.</Box>
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', color: palette.tokens.muted, lineHeight: 1.7, mb: 4, maxWidth: 460 }}>
            From uploading your first certificate to renewing the last one — Mill, your AI agent, handles the entire lifecycle so you can focus on your properties, not your paperwork.
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.mutedSoft }}>
            Hover over each step to learn more
          </Typography>
        </Box>

        {/* ── RIGHT: flow ── */}
        <Box sx={{ flex: '0 0 50%', maxWidth: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { lg: 6 }, pl: 0 }}>

          {/* Step 1: Upload (one-off) */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              backgroundColor: palette.tokens.white,
              border: `1.5px solid ${palette.tokens.hairline}`,
              borderRadius: '14px',
              p: 1.75,
              width: 308,
              transition: 'all 0.2s',
              '&:hover': { borderColor: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,0.12)' },
            }}
          >
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: UPLOAD_STEP.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {UPLOAD_STEP.icon}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.2 }}>One-time setup</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: palette.tokens.ink }}>{UPLOAD_STEP.title}</Typography>
            </Box>
          </Box>

          {/* Connector gap above circle */}
          <Box sx={{ height: CONNECTOR_H, display: 'flex', justifyContent: 'center' }}>
            <svg width="2" height={CONNECTOR_H} style={{ overflow: 'visible' }}>
              <line x1="1" y1="0" x2="1" y2={CONNECTOR_H} stroke="#6366f1" strokeWidth="2" strokeDasharray="5 5"
                style={{ animation: 'aiFlowDown 0.7s linear infinite' }} />
            </svg>
          </Box>

          {/* Pentagon circle */}
          <Box sx={{ position: 'relative', width: CONTAINER, height: CONTAINER, mt: '-80px' }}>

            {/* Animated dashed circle + bridge line from container top to first cycle card */}
            <svg width={CONTAINER} height={CONTAINER} style={{ position: 'absolute', top: 0, left: 0 }}>
              <defs>
                <radialGradient id="aiZoneFill" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#8b5cf6" stopOpacity="0.07" />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
                </radialGradient>
              </defs>

              {/* AI zone: soft filled background */}
              <circle cx={CX} cy={CY} r={RADIUS} fill="url(#aiZoneFill)" />

              {/* Bridge: continues the connector line from box top down to the topmost cycle card */}
              <line
                x1={CX} y1={BRIDGE_Y1} x2={CX} y2={INNER_BRIDGE}
                stroke="#6366f1" strokeWidth="2" strokeDasharray="5 5"
                style={{ animation: 'aiFlowDown 0.7s linear infinite' }}
              />

              {/* Animated dashed border */}
              <circle
                cx={CX} cy={CY} r={RADIUS}
                fill="none"
                stroke="#c7d2fe"
                strokeWidth="2"
                strokeDasharray="7 7"
                style={{ animation: `circleFlowCW 22s linear infinite` }}
              />

            </svg>

            {/* Centre: default label or hovered description */}
            <Box
              sx={{
                position: 'absolute',
                left: CX,
                top: CY,
                width: 198,
                transform: 'translate(-50%, -110%)',
                textAlign: 'center',
                pointerEvents: 'none',
                transition: 'opacity 0.2s',
              }}
            >
              {hoveredIdx === null ? (
                <>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#6366f1', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.5 }}>
                    Mill, your AI Agent
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: palette.tokens.muted, lineHeight: 1.5 }}>
                    Zero effort. Every renewal.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: '13px', color: palette.tokens.body, lineHeight: 1.6, fontWeight: 500 }}>
                  {CYCLE_STEPS[hoveredIdx].desc}
                </Typography>
              )}
            </Box>

            {/* 5 cards on the circle */}
            {CYCLE_STEPS.map((step, i) => {
              const angle = -Math.PI / 2 + (2 * Math.PI * i) / 5;
              const cx = CX + RADIUS * Math.cos(angle);
              const cy = CY + RADIUS * Math.sin(angle);
              const isHovered = hoveredIdx === i;

              return (
                <Box
                  key={step.title}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  sx={{
                    position: 'absolute',
                    left: cx - CARD_W / 2,
                    top: cy - CARD_H_HALF,
                    width: CARD_W,
                    height: CARD_H,
                    overflow: 'hidden',
                    backgroundColor: palette.tokens.white,
                    border: `1.5px solid ${isHovered ? step.accent : palette.tokens.hairline}`,
                    borderRadius: '12px',
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'default',
                    transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                    zIndex: 1,
                    boxShadow: isHovered ? `0 4px 16px ${step.accent}30` : '0 1px 4px rgba(0,0,0,0.05)',
                    transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 39, height: 39, borderRadius: '10px', background: step.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {step.icon}
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: isHovered ? step.accent : palette.tokens.ink, lineHeight: 1.25, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {step.title}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AIFlowPage;
